import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useAuth } from '../../context/AuthContext';
import { useReminders, useReminderCategories, useReminderTags, useDeleteReminder } from '../../../hooks/useApi';
import ReminderCard from './components/ReminderCard';
import { IconPlus, IconFilter, IconSearch, IconCalendar, IconRefreshCw } from '../../components/icons';

const ReminderList = () => {
    const { t } = useI18n(['common', 'reminders']);
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Filters
    const [filters, setFilters] = useState({
        status: searchParams.get('status') || 'active',
        category: searchParams.get('category') || '',
        tag: searchParams.get('tag') || '',
        search: searchParams.get('search') || '',
        view: searchParams.get('view') || 'all' // 'all', 'owned', 'shared'
    });
    
    const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
    const [showFilters, setShowFilters] = useState(false);
    const [page, setPage] = useState(1);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filters.search);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [filters.search]);

    // Build query params
    const queryParams = useMemo(() => ({
        status: filters.status || undefined,
        category: filters.category || undefined,
        tag: filters.tag || undefined,
        search: debouncedSearch || undefined,
        page,
        limit: 20
    }), [filters.status, filters.category, filters.tag, debouncedSearch, page]);

    // React Query hooks
    const { 
        data: remindersResult, 
        isLoading, 
        isFetching,
        error,
        refetch 
    } = useReminders(queryParams);
    
    const { data: categoriesResult } = useReminderCategories();
    const { data: tagsResult } = useReminderTags();
    const deleteMutation = useDeleteReminder();

    // Process data
    const allReminders = remindersResult?.data?.reminders || [];
    const pagination = remindersResult?.data?.pagination || { page: 1, total: 0, totalPages: 0 };
    const categories = categoriesResult?.data?.categories || [];
    const tags = tagsResult?.data?.tags || [];

    // Apply client-side view filter
    const reminders = useMemo(() => {
        if (filters.view === 'owned') {
            return allReminders.filter(r => r.isOwner);
        } else if (filters.view === 'shared') {
            return allReminders.filter(r => !r.isOwner);
        }
        return allReminders;
    }, [allReminders, filters.view]);

    // Update URL params when filters change
    useEffect(() => {
        const params = new URLSearchParams();
        if (filters.status && filters.status !== 'active') params.set('status', filters.status);
        if (filters.category) params.set('category', filters.category);
        if (filters.tag) params.set('tag', filters.tag);
        if (filters.search) params.set('search', filters.search);
        if (filters.view && filters.view !== 'all') params.set('view', filters.view);
        setSearchParams(params);
    }, [filters, setSearchParams]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const handleDelete = async (id) => {
        if (!confirm(t('deleteConfirm', { ns: 'reminders' }))) return;
        
        try {
            await deleteMutation.mutateAsync(id);
        } catch (err) {
            alert(err.message || t('error', { ns: 'common' }));
        }
    };

    const statusOptions = [
        { value: 'active', label: t('status.active', { ns: 'reminders' }) },
        { value: 'paused', label: t('status.paused', { ns: 'reminders' }) },
        { value: 'completed', label: t('status.completed', { ns: 'reminders' }) },
        { value: 'archived', label: t('status.archived', { ns: 'reminders' }) },
        { value: '', label: t('status.all', { ns: 'reminders' }) }
    ];

    const viewOptions = [
        { value: 'all', label: t('view.all', { ns: 'reminders' }) },
        { value: 'owned', label: t('view.owned', { ns: 'reminders' }) },
        { value: 'shared', label: t('view.shared', { ns: 'reminders' }) }
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {t('title', { ns: 'reminders' })}
                    </h1>
                    <p className="text-gray-600 mt-1">
                        {t('subtitle', { ns: 'reminders' })}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        <IconRefreshCw className={`w-4 h-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
                        {t('refresh', { ns: 'common' })}
                    </button>
                    <Link
                        to="/reminders/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                        <IconPlus className="w-4 h-4 mr-1" />
                        {t('create', { ns: 'reminders' })}
                    </Link>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4">
                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                placeholder={t('searchPlaceholder', { ns: 'reminders' })}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    {/* View Toggle */}
                    <div className="flex rounded-md shadow-sm">
                        {viewOptions.map(option => (
                            <button
                                key={option.value}
                                onClick={() => handleFilterChange('view', option.value)}
                                className={`px-3 py-2 text-sm font-medium border first:rounded-l-md last:rounded-r-md -ml-px first:ml-0 ${
                                    filters.view === option.value
                                        ? 'bg-blue-600 text-white border-blue-600 z-10'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    {/* Status Filter */}
                    <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    {/* Toggle More Filters */}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                            showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                        <IconFilter className="w-4 h-4 mr-1" />
                        {t('filters', { ns: 'common' })}
                    </button>
                </div>

                {/* Extended Filters */}
                {showFilters && (
                    <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4">
                        {/* Category Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('category', { ns: 'reminders' })}
                            </label>
                            <select
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">{t('allCategories', { ns: 'reminders' })}</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        {/* Tag Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('tag', { ns: 'reminders' })}
                            </label>
                            <select
                                value={filters.tag}
                                onChange={(e) => handleFilterChange('tag', e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">{t('allTags', { ns: 'reminders' })}</option>
                                {tags.map(tag => (
                                    <option key={tag} value={tag}>{tag}</option>
                                ))}
                            </select>
                        </div>

                        {/* Clear Filters */}
                        <div className="flex items-end">
                            <button
                                onClick={() => setFilters({ status: 'active', category: '', tag: '', search: '', view: 'all' })}
                                className="text-sm text-blue-600 hover:text-blue-800"
                            >
                                {t('clearFilters', { ns: 'common' })}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                    {error.message || t('error', { ns: 'common' })}
                </div>
            ) : reminders.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <IconCalendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t('noReminders', { ns: 'reminders' })}
                    </h3>
                    <p className="text-gray-500 mb-6">
                        {t('noRemindersDesc', { ns: 'reminders' })}
                    </p>
                    <Link
                        to="/reminders/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                        <IconPlus className="w-4 h-4 mr-1" />
                        {t('createFirst', { ns: 'reminders' })}
                    </Link>
                </div>
            ) : (
                <>
                    {/* Reminders Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {reminders.map(reminder => (
                            <ReminderCard
                                key={reminder._id}
                                reminder={reminder}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex justify-center items-center mt-8 gap-2">
                            <button
                                onClick={() => setPage(prev => prev - 1)}
                                disabled={page <= 1}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                {t('previous', { ns: 'common' })}
                            </button>
                            <span className="text-sm text-gray-600">
                                {t('pageOf', { ns: 'common', page: pagination.page, total: pagination.totalPages })}
                            </span>
                            <button
                                onClick={() => setPage(prev => prev + 1)}
                                disabled={page >= pagination.totalPages}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                {t('next', { ns: 'common' })}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ReminderList;
